import React, { useEffect, useRef, useState, useMemo } from 'react';
import useOrgJwt from '../hooks/useOrgJwt';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { ToggleButton } from 'primereact/togglebutton';
import { Toast } from 'primereact/toast';

import 'primereact/resources/themes/lara-dark-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './RedactionRules.css';

import { formatDate } from '../utils/dateFormatter';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const BUILTIN_RULES = [
  { id: 'builtin-ssn',   pattern: '\\b\\d{3}-\\d{2}-\\d{4}\\b',                      replacement: '[REDACTED_SSN]',         enabled: true,  __builtin: true },
  { id: 'builtin-cc',    pattern: '\\b(?:\\d[ -]*?){13,16}\\b',                      replacement: '[REDACTED_CREDIT_CARD]', enabled: true,  __builtin: true },
  { id: 'builtin-email', pattern: '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}', replacement: '[REDACTED_EMAIL]',      enabled: true,  __builtin: true },
  { id: 'builtin-phone', pattern: '\\b\\d{10}\\b',                                   replacement: '[REDACTED_PHONE]',       enabled: true,  __builtin: true }
];

export default function RedactionRules() {
  const { orgJwt, tenantId, role } = useOrgJwt();
  const toast = useRef(null);

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showEdit, setShowEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [pattern, setPattern] = useState('');
  const [replacement, setReplacement] = useState('');
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null); // Track which rule is being toggled

  // Billing/plan state (aligned with Settings page behavior)
  const [tier, setTier] = useState('free'); // 'free' | 'pro' | 'enterprise'
  const isAdmin = role === 'admin';
  const isPaid = tier !== 'free';

  const authz = () => ({ Authorization: `Bearer ${orgJwt}` });

  const load = async () => {
    if (!tenantId || !orgJwt) return;
    setLoading(true);
    try {
      // Load custom rules
      const res = await fetch(`${API_BASE}/organizations/${tenantId}/redaction-rules`, { headers: authz() });
      if (!res.ok) throw new Error('Failed to load rules');
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.rules || []);
      setRules(list);

      // Fetch billing/me like Settings to determine tier
      try {
        const me = await fetch(`${API_BASE}/billing/me`, { headers: authz(), credentials: 'omit' });
        if (me.ok) {
          const txt = await me.text();
          if (txt) {
            try {
              const j = JSON.parse(txt);
              const sub = j?.subscription || {};
              setTier((sub.tier || 'free').toLowerCase());
            } catch {}
          }
        }
      } catch {}
    } catch (e) {
      setRules([]);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: e.message, life: 4000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, orgJwt]);

  const openNew = () => {
    setEditId(null);
    setPattern('');
    setReplacement('');
    setShowEdit(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setPattern(row.pattern || '');
    setReplacement(row.replacement || '');
    setShowEdit(true);
  };

  const save = async () => {
    if (!tenantId || !pattern) return;
    setSaving(true);
    try {
      if (editId) {
        const res = await fetch(`${API_BASE}/organizations/${tenantId}/redaction-rules/${editId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authz() },
          body: JSON.stringify({ Pattern: pattern, Replacement: replacement })
        });
        if (!res.ok) throw new Error('Failed to save');
      } else {
        const res = await fetch(`${API_BASE}/organizations/${tenantId}/redaction-rules`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authz() },
          body: JSON.stringify({ Pattern: pattern, Replacement: replacement })
        });
        if (!res.ok) throw new Error('Failed to create');
      }
      setShowEdit(false);
      setPattern('');
      setReplacement('');
      load();
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: e.message, life: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (row) => {
    if (row.__builtin) return;
    setTogglingId(row.id);
    try {
      const res = await fetch(`${API_BASE}/organizations/${tenantId}/redaction-rules/${row.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authz() },
        body: JSON.stringify({ Enabled: !row.enabled })
      });
      if (!res.ok) throw new Error('Failed to update');
      load();
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: e.message, life: 4000 });
    } finally {
      setTogglingId(null);
    }
  };

  const remove = async (row) => {
    // For built-ins on paid we surface a trash icon per your request; if your API does not accept deleting a built-in id,
    // you may want to treat this as "disable/override" server-side.
    try {
      const res = await fetch(`${API_BASE}/organizations/${tenantId}/redaction-rules/${row.id}`, {
        method: 'DELETE',
        headers: authz()
      });
      if (!res.ok) throw new Error('Failed to delete');
      load();
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: e.message, life: 4000 });
    }
  };

  const displayedRules = useMemo(
    () => [...BUILTIN_RULES, ...(Array.isArray(rules) ? rules : [])],
    [rules]
  );

  const actions = (row) => {
    if (row.__builtin) {
      if (!isPaid || !isAdmin) return <span className="text-xs opacity-70">Built-in</span>;
      return (
        <div className="flex gap-2">
          <Button icon="pi pi-pencil" rounded text onClick={() => openEdit(row)} />
          <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => remove(row)} />
        </div>
      );
    }
    // Custom rules - require paid tier AND admin role
    const canModify = isAdmin && isPaid;
    return (
      <div className="flex gap-2">
        <Button icon="pi pi-pencil" rounded text onClick={() => openEdit(row)} disabled={!canModify} />
        <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => remove(row)} disabled={!canModify} />
      </div>
    );
  };

  const enabledBody = (row) => {
    if (row.__builtin) {
      return (
        <ToggleButton
          checked={true}
          onChange={() => {}}
          onIcon="pi pi-check"
          offIcon="pi pi-times"
          disabled
        />
      );
    }
    const isToggling = togglingId === row.id;
    const canModify = isAdmin && isPaid;
    return (
      <ToggleButton
        checked={!!row.enabled}
        onChange={() => toggleEnabled(row)}
        onIcon={isToggling ? "pi pi-spin pi-spinner" : "pi pi-check"}
        offIcon={isToggling ? "pi pi-spin pi-spinner" : "pi pi-times"}
        disabled={!canModify || isToggling}
      />
    );
  };

  const footer = (
    <div className="flex justify-end gap-2 w-full">
      <Button label="Cancel" outlined onClick={() => setShowEdit(false)} />
      <Button label={editId ? 'Save' : 'Create'} onClick={save} loading={saving} disabled={!pattern || !isAdmin || !isPaid} />
    </div>
  );

  return (
    <div className="rules-page">
      <Toast ref={toast} />

      <h1 className="page-title">Redaction Rules</h1>
      <p className="page-help">
        Add regex patterns to redact sensitive data before it’s logged. Each rule has a JavaScript-style regular
        expression in <span className="mono">pattern</span> and a static <span className="mono">replacement</span>.
        The first four rows are built-in. Free accounts cannot modify these; paid accounts can edit or disable them.
      </p>

      <div className="card rules-card rounded-2xl shadow-2">
        <DataTable
          value={displayedRules}
          loading={loading}
          responsiveLayout="scroll"
          className="p-datatable-sm rules-table"
          stripedRows
          showGridlines
          emptyMessage="No custom rules yet"
        >
          <Column field="pattern" header="Pattern" />
          <Column field="replacement" header="Replacement" />
          <Column header="Enabled" body={enabledBody} />
          <Column header="Created" body={(r) => formatDate(r.created_at, 'table')} />
          <Column header="Updated" body={(r) => formatDate(r.updated_at, 'table')} />
          <Column header="Actions" body={actions} />
        </DataTable>
      </div>

      <div className="below-actions">
        {!isPaid && (
          <p className="text-sm text-zinc-400 mb-3" style={{ fontStyle: 'italic' }}>
            Custom rules require a Pro or Enterprise subscription
          </p>
        )}
        <Button 
          label="New Rule" 
          icon="pi pi-plus" 
          onClick={openNew} 
          disabled={!isAdmin || !isPaid}
          tooltip={!isPaid ? "Upgrade to Pro or Enterprise to create custom redaction rules" : undefined}
          tooltipOptions={{ position: 'top' }}
        />
      </div>

      <Dialog
        header={editId ? 'Edit Rule' : 'New Rule'}
        visible={showEdit}
        onHide={() => setShowEdit(false)}
        footer={footer}
        style={{ width: '32rem' }}
        className="rules-dialog"
      >
        <div className="flex flex-col">
          <div className="form-field">
            <label htmlFor="pattern">Regex Pattern</label>
            <InputText id="pattern" value={pattern} onChange={(e) => setPattern(e.target.value)} />
          </div>
          <div className="form-field">
            <label htmlFor="replacement">Replacement</label>
            <InputText id="replacement" value={replacement} onChange={(e) => setReplacement(e.target.value)} />
          </div>
        </div>
      </Dialog>
    </div>
  );
}